/**
 * AdminDashboard.js - Enhanced Admin Dashboard with Widgets
 * 
 * Features:
 * - Welcome banner with date
 * - Today's schedule overview with coverage status
 * - This week's PTO summary
 * - Schedule issues requiring attention (ratio violations)
 * - Quick stats cards (Students link to student management)
 * 
 * MOBILE FEATURES (new):
 * - Weekly Teacher Roster (like student roster, no print)
 * - Out This Week card
 * - Schedule Issues card
 * - PTO Management with filtering and accept/deny
 * - Monthly Team PTO overview
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import dataStore from '../data/dataStore';

function AdminDashboard() {
  const { user } = useAuth();
  const { theme, colors, darkMode } = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // State for dashboard data
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeachers: 0,
    totalRooms: 0,
    totalStudents: 0,
    pendingPTO: 0
  });
  
  const [todaySchedule, setTodaySchedule] = useState({
    date: '',
    dayName: '',
    roomCoverage: [],
    totalAssignments: 0
  });
  
  const [weekPTO, setWeekPTO] = useState([]);
  const [pendingPTOList, setPendingPTOList] = useState([]);
  const [ratioViolations, setRatioViolations] = useState([]);
  
  // Mobile-specific state
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [weeklyTeacherRoster, setWeeklyTeacherRoster] = useState({});
  const [selectedRosterDay, setSelectedRosterDay] = useState('M');
  const [expandedRooms, setExpandedRooms] = useState({}); // For mobile collapsible rooms
  const [allPTORequests, setAllPTORequests] = useState([]);
  const [ptoFilter, setPtoFilter] = useState('Pending');
  const [message, setMessage] = useState('');
  const [teamPTOMonth, setTeamPTOMonth] = useState(new Date());
  const [teamPTOEntries, setTeamPTOEntries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);

  // Helper: Get Monday of current week
  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
  }

  // Load all dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Load mobile-specific data when week changes
  useEffect(() => {
    if (isMobile) {
      loadMobileData();
    }
  }, [weekStart, isMobile]);

  // Load team PTO when month changes
  useEffect(() => {
    if (isMobile) {
      loadTeamPTO();
    }
  }, [teamPTOMonth, isMobile]);

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);
  
  const loadDashboardData = () => {
    // Basic stats
    const allUsers = dataStore.getUsers();
    const allRooms = dataStore.getRooms();
    const students = dataStore.getActiveChildren();
    const pendingPTO = dataStore.getPTORequestsByStatus('Pending');
    
    setUsers(allUsers);
    setRooms(allRooms);
    setAllPTORequests(dataStore.getPTORequests());
    
    setStats({
      totalUsers: allUsers.length,
      totalTeachers: allUsers.filter(u => u.role === 'USER').length,
      totalRooms: allRooms.length,
      totalStudents: students.length,
      pendingPTO: pendingPTO.length
    });
    
    // Today's schedule
    const today = new Date();
    const todayStr = formatDateStr(today);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const schedule = dataStore.getScheduleForDate(todayStr);
    
    // Calculate room coverage
    const roomCoverage = allRooms.map(room => {
      const assignments = schedule[room.id] || [];
      return {
        roomId: room.id,
        roomName: room.name,
        assignmentCount: assignments.length,
        teachers: assignments.map(a => {
          const teacher = dataStore.getUserById(a.userId);
          return teacher?.name || 'Unknown';
        })
      };
    });
    
    const totalAssignments = roomCoverage.reduce((sum, r) => sum + r.assignmentCount, 0);
    
    setTodaySchedule({
      date: todayStr,
      dayName: dayNames[today.getDay()],
      roomCoverage,
      totalAssignments
    });
    
    // This week's PTO
    const weekDates = getWeekDates(today);
    const allPTO = dataStore.getPTORequestsByStatus('Approved');
    const weekPTOEntries = [];
    
    allPTO.forEach(pto => {
      const ptoUser = dataStore.getUserById(pto.userId);
      pto.dates.forEach(date => {
        if (weekDates.includes(date)) {
          weekPTOEntries.push({
            date,
            userName: ptoUser?.name || 'Unknown',
            oderId: pto.userId
          });
        }
      });
    });
    
    // Sort by date
    weekPTOEntries.sort((a, b) => a.date.localeCompare(b.date));
    setWeekPTO(weekPTOEntries);
    
    // Pending PTO with details
    const pendingWithDetails = pendingPTO.map(pto => {
      const ptoUser = dataStore.getUserById(pto.userId);
      return {
        ...pto,
        userName: ptoUser?.name || 'Unknown'
      };
    });
    setPendingPTOList(pendingWithDetails);
    
    // Check for ratio violations this week
    const violations = [];
    weekDates.forEach(dateStr => {
      const dayViolations = dataStore.validateDayRatios(dateStr);
      dayViolations.forEach(v => {
        violations.push({
          date: dateStr,
          roomName: v.room.name,
          roomId: v.room.id,
          childCount: v.ratioStatus.childCount,
          maxChildren: v.ratioStatus.maxChildren,
          teacherCount: v.ratioStatus.teacherCount
        });
      });
    });
    setRatioViolations(violations);
  };

  // Load mobile-specific data
  const loadMobileData = () => {
    const allRooms = dataStore.getRooms();
    const allUsers = dataStore.getUsers();
    const weekDatesArr = dataStore.getWeekDates(weekStart);
    const dayNames = ['M', 'T', 'W', 'Th', 'F'];
    
    // Build weekly teacher roster (like student roster)
    const rosterByDay = {};
    
    dayNames.forEach((dayName, dayIndex) => {
      const dateStr = weekDatesArr[dayIndex];
      const daySchedule = dataStore.getScheduleForDate(dateStr);
      const childSchedule = dataStore.getChildScheduleForWeek(weekDatesArr[0]);
      const rosterByRoom = {};
      
      allRooms.forEach(room => {
        const assignments = daySchedule[room.id] || [];
        
        // Count students in this room for this date
        const dayChildSchedule = childSchedule[dateStr] || {};
        const studentCount = Object.values(dayChildSchedule).filter(
          entry => entry.roomId === room.id && !entry.isAbsent
        ).length;
        
        if (assignments.length > 0 || studentCount > 0) {
          const teachers = assignments.map(a => {
            const teacher = allUsers.find(u => u.id === a.userId);
            return {
              id: a.userId,
              name: teacher?.name || 'Unknown',
              startTime: a.startTime,
              endTime: a.endTime
            };
          }).sort((a, b) => a.startTime.localeCompare(b.startTime));
          
          rosterByRoom[room.id] = {
            roomName: room.name,
            teachers,
            studentCount,
            isUtility: room.isUtility
          };
        }
      });
      
      rosterByDay[dayName] = rosterByRoom;
    });
    
    setWeeklyTeacherRoster(rosterByDay);
    
    // Initialize all rooms as expanded
    const initialExpanded = {};
    allRooms.forEach(room => {
      initialExpanded[room.id] = true;
    });
    setExpandedRooms(initialExpanded);
    
    // Set selected day to today if in current week, otherwise Monday
    const todayStr = formatDateStr(new Date());
    const todayIndex = weekDatesArr.indexOf(todayStr);
    if (todayIndex >= 0) {
      setSelectedRosterDay(dayNames[todayIndex]);
    } else {
      setSelectedRosterDay('M');
    }
  };

  // Load team PTO for the selected month
  const loadTeamPTO = () => {
    const allUsers = dataStore.getUsers();
    const approvedPTO = dataStore.getPTORequestsByStatus('Approved');
    
    const year = teamPTOMonth.getFullYear();
    const month = teamPTOMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const firstDayStr = formatDateStr(firstDay);
    const lastDayStr = formatDateStr(lastDay);
    
    // Collect all dates by user
    const datesByUser = {};
    
    approvedPTO.forEach(pto => {
      const ptoUser = allUsers.find(u => u.id === pto.userId);
      if (!ptoUser) return;
      
      const datesInMonth = pto.dates.filter(d => d >= firstDayStr && d <= lastDayStr);
      
      datesInMonth.forEach(date => {
        if (!datesByUser[pto.userId]) {
          datesByUser[pto.userId] = {
            userName: ptoUser.name,
            dates: [],
            partialDays: {}
          };
        }
        
        if (!datesByUser[pto.userId].dates.includes(date)) {
          datesByUser[pto.userId].dates.push(date);
        }
        
        if (pto.isPartialDay && pto.dates.length === 1 && pto.dates[0] === date) {
          datesByUser[pto.userId].partialDays[date] = {
            startTime: pto.partialStartTime,
            hours: pto.partialHours
          };
        }
      });
    });
    
    // Group consecutive dates into ranges
    const entries = [];
    
    Object.entries(datesByUser).forEach(([odInner, userData]) => {
      const sortedDates = [...userData.dates].sort();
      
      if (sortedDates.length === 0) return;
      
      let rangeStart = sortedDates[0];
      let rangeEnd = sortedDates[0];
      
      const addRange = () => {
        const isPartial = rangeStart === rangeEnd && userData.partialDays[rangeStart];
        const partialInfo = isPartial ? userData.partialDays[rangeStart] : null;
        
        entries.push({
          id: `${odInner}-${rangeStart}`,
          userName: userData.userName,
          startDate: rangeStart,
          endDate: rangeEnd,
          isPartialDay: isPartial,
          partialStartTime: partialInfo?.startTime,
          partialHours: partialInfo?.hours
        });
      };
      
      for (let i = 1; i < sortedDates.length; i++) {
        const currentDate = new Date(sortedDates[i] + 'T12:00:00');
        const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
        const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
        
        if (diffDays === 1) {
          rangeEnd = sortedDates[i];
        } else {
          addRange();
          rangeStart = sortedDates[i];
          rangeEnd = sortedDates[i];
        }
      }
      addRange();
    });
    
    entries.sort((a, b) => a.startDate.localeCompare(b.startDate));
    setTeamPTOEntries(entries);
  };

  // Helper: Format date as YYYY-MM-DD
  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper: Get this week's dates (Mon-Fri)
  const getWeekDates = (date) => {
    const curr = new Date(date);
    const day = curr.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(curr);
    monday.setDate(curr.getDate() + diff);
    
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      dates.push(formatDateStr(d));
    }
    return dates;
  };
  
  // Helper: Format date for display (MM/DD)
  const formatDateDisplay = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}`;
  };

  // Helper: Format date as MM/DD/YYYY
  const formatDateShort = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Helper: Format time from 24h to 12h
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Format week label
  const formatWeekLabel = () => {
    const startDate = new Date(weekStart + 'T12:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 4);
    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Week navigation
  const prevWeek = () => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() - 7);
    setWeekStart(formatDateStr(d));
  };

  const nextWeek = () => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + 7);
    setWeekStart(formatDateStr(d));
  };

  const goToCurrentWeek = () => {
    setWeekStart(getMonday(new Date()));
  };

  const isCurrentWeek = () => {
    return weekStart === getMonday(new Date());
  };

  // Toggle room expansion for weekly roster (mobile)
  const toggleRoomExpanded = (roomId) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Team PTO month navigation
  const prevPTOMonth = () => {
    setTeamPTOMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const nextPTOMonth = () => {
    setTeamPTOMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  // PTO actions
  const handleApprovePTO = async (requestId) => {
    try {
      await dataStore.approvePTORequest(requestId, user);
      setAllPTORequests(dataStore.getPTORequests());
      loadDashboardData();
      if (isMobile) loadTeamPTO();
      setMessage('✅ PTO request approved!');
    } catch (err) {
      console.error('Error approving PTO:', err);
      setMessage('❌ Error: ' + err.message);
    }
  };

  const handleDenyPTO = async (requestId) => {
    try {
      await dataStore.denyPTORequest(requestId, user);
      setAllPTORequests(dataStore.getPTORequests());
      loadDashboardData();
      setMessage('PTO request denied.');
    } catch (err) {
      console.error('Error denying PTO:', err);
      setMessage('❌ Error: ' + err.message);
    }
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const u = users.find(x => x.id === userId);
    return u ? u.name : 'Unknown';
  };

  // Format date ranges for PTO display
  const formatDateRanges = (dates) => {
    if (!dates || dates.length === 0) return '-';
    
    const sortedDates = [...dates].sort();
    const ranges = [];
    let rangeStart = sortedDates[0];
    let rangeEnd = sortedDates[0];
    
    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = new Date(sortedDates[i] + 'T12:00:00');
      const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
      const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (diffDays === 1) {
        rangeEnd = sortedDates[i];
      } else {
        ranges.push({ start: rangeStart, end: rangeEnd });
        rangeStart = sortedDates[i];
        rangeEnd = sortedDates[i];
      }
    }
    ranges.push({ start: rangeStart, end: rangeEnd });
    
    return ranges.map(range => {
      if (range.start === range.end) {
        return formatDateShort(range.start);
      } else {
        return `${formatDateShort(range.start)} - ${formatDateShort(range.end)}`;
      }
    }).join(', ');
  };

  // Filtered PTO requests
  const filteredPTORequests = allPTORequests
    .filter(req => ptoFilter === 'All' || req.status === ptoFilter)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map(req => ({
      ...req,
      userName: getUserName(req.userId)
    }));

  // Count by status
  const pendingCount = allPTORequests.filter(r => r.status === 'Pending').length;
  const approvedCount = allPTORequests.filter(r => r.status === 'Approved').length;
  const deniedCount = allPTORequests.filter(r => r.status === 'Denied').length;

  // =============================================
  // MOBILE STYLES
  // =============================================
  
  const mobileContainerStyle = {
    padding: '12px',
    maxWidth: '100%',
    margin: '0 auto'
  };

  const mobileCardStyle = {
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: `1px solid ${theme.border}`
  };

  const mobileCardTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const dayTabsStyle = {
    display: 'flex',
    gap: '4px',
    marginBottom: '12px'
  };

  const dayTabStyle = (isSelected) => ({
    flex: 1,
    padding: '8px 4px',
    backgroundColor: isSelected ? colors.blue : 'transparent',
    color: isSelected ? (darkMode ? '#fff' : '#333') : theme.text,
    border: isSelected ? 'none' : `1px solid ${theme.border}`,
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: isSelected ? '600' : '400',
    fontSize: '0.8rem',
    textAlign: 'center'
  });

  const filterTabStyle = (isActive) => ({
    padding: '6px 12px',
    borderRadius: '20px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: '600',
    backgroundColor: isActive ? colors.blue : theme.backgroundSecondary,
    color: isActive ? (darkMode ? '#fff' : '#333') : theme.text
  });

  const ptoRequestCardStyle = {
    backgroundColor: theme.backgroundSecondary,
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '10px',
    border: `1px solid ${theme.border}`
  };

  const actionButtonStyle = (color) => ({
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: color,
    color: darkMode ? '#fff' : '#333',
    fontSize: '0.85rem',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1
  });

  // =============================================
  // DESKTOP STYLES
  // =============================================
  
  const containerStyle = {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto'
  };
  
  const headerBannerStyle = {
    background: darkMode 
      ? 'linear-gradient(135deg, rgba(30,30,50,0.95), rgba(40,40,60,0.9))'
      : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,245,250,0.9))',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '24px 28px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  };
  
  const welcomeRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };
  
  const welcomeTitleStyle = {
    fontSize: '1.6rem',
    fontWeight: '600',
    color: theme.text,
    margin: '0 0 4px 0'
  };
  
  const dateStyle = {
    fontSize: '0.95rem',
    color: theme.textSecondary
  };
  
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)',
    gap: '20px'
  };
  
  const widgetStyle = (span = 4) => ({
    gridColumn: `span ${span}`,
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  });
  
  const widgetHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  };
  
  const widgetTitleStyle = {
    fontSize: '1rem',
    fontWeight: '600',
    color: theme.text,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };
  
  const viewAllLinkStyle = {
    fontSize: '0.8rem',
    color: colors.blue,
    cursor: 'pointer',
    textDecoration: 'none'
  };
  
  const statsRowStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px'
  };
  
  const statCardStyle = (accentColor) => ({
    flex: 1,
    backgroundColor: theme.cardBackground,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderLeft: `4px solid ${accentColor}`,
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s'
  });
  
  const statValueStyle = (color) => ({
    fontSize: '2rem',
    fontWeight: '700',
    color: color,
    marginBottom: '4px'
  });
  
  const statLabelStyle = {
    fontSize: '0.85rem',
    color: theme.textSecondary
  };
  
  const roomRowStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${theme.border}`
  };
  
  const roomNameStyle = {
    flex: 1,
    fontSize: '0.9rem',
    fontWeight: '500',
    color: theme.text
  };
  
  const coverageBadgeStyle = (count) => ({
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    backgroundColor: count > 0 ? `${colors.green}20` : `${colors.red}20`,
    color: count > 0 ? colors.green : colors.red
  });

  const ptoItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${theme.border}`,
    gap: '12px'
  };
  
  const ptoDateStyle = {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: colors.blue,
    minWidth: '50px'
  };
  
  const ptoNameStyle = {
    fontSize: '0.9rem',
    color: theme.text
  };
  
  const pendingItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    borderRadius: '8px',
    marginBottom: '8px'
  };
  
  const pendingInfoStyle = {
    flex: 1
  };
  
  const pendingNameStyle = {
    fontSize: '0.9rem',
    fontWeight: '500',
    color: theme.text
  };
  
  const pendingDatesStyle = {
    fontSize: '0.8rem',
    color: theme.textSecondary
  };
  
  const reviewButtonStyle = {
    padding: '6px 14px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: colors.blue,
    color: '#fff',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer'
  };
  
  const emptyStateStyle = {
    padding: '20px',
    textAlign: 'center',
    color: theme.textSecondary,
    fontSize: '0.9rem'
  };

  const messageStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    marginBottom: '12px',
    textAlign: 'center',
    backgroundColor: message.includes('✅') ? colors.green : colors.red,
    color: darkMode ? '#1a1a2e' : '#fff',
    fontWeight: '600',
    fontSize: '0.9rem'
  };


  // =============================================
  // MOBILE RENDER
  // =============================================
  
  if (isMobile) {
    const today = new Date();
    const dateDisplay = today.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric'
    });

    return (
      <div style={mobileContainerStyle}>
        {/* Welcome Banner */}
        <div style={{ ...mobileCardStyle, marginBottom: '12px' }}>
          <h2 style={{ 
            fontSize: '1.1rem', 
            fontWeight: '600', 
            color: theme.text, 
            margin: '0 0 4px 0' 
          }}>
            Welcome, {user?.name}! 👋
          </h2>
          <p style={{ 
            fontSize: '0.85rem', 
            color: theme.textSecondary, 
            margin: 0 
          }}>
            {dateDisplay}
          </p>
        </div>

        {/* Message Toast */}
        {message && <div style={messageStyle}>{message}</div>}

        {/* Quick Stats Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '10px', 
          marginBottom: '16px' 
        }}>
          <div style={{
            ...mobileCardStyle,
            marginBottom: 0,
            borderLeft: `3px solid ${colors.green}`,
            padding: '12px'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.green }}>
              {stats.totalStudents}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Students</div>
          </div>
          <div style={{
            ...mobileCardStyle,
            marginBottom: 0,
            borderLeft: `3px solid ${stats.pendingPTO > 0 ? '#FF8A65' : colors.green}`,
            padding: '12px'
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700', 
              color: stats.pendingPTO > 0 ? '#FF8A65' : colors.green 
            }}>
              {stats.pendingPTO}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>Pending PTO</div>
          </div>
        </div>

        {/* Weekly Teacher Roster Card */}
        <div style={mobileCardStyle}>
          <div style={{ ...mobileCardTitleStyle, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>👥</span>
              Weekly Teacher Roster
            </div>
          </div>

          {/* Week Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <button 
              onClick={prevWeek}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: colors.blue,
                color: darkMode ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              ‹
            </button>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: theme.text }}>
                {formatWeekLabel()}
              </div>
              {!isCurrentWeek() && (
                <button 
                  onClick={goToCurrentWeek}
                  style={{
                    marginTop: '4px',
                    padding: '2px 8px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: theme.textSecondary,
                    cursor: 'pointer',
                    fontSize: '0.7rem'
                  }}
                >
                  Today
                </button>
              )}
            </div>

            <button 
              onClick={nextWeek}
              style={{
                padding: '8px 12px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: colors.blue,
                color: darkMode ? '#fff' : '#333',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              ›
            </button>
          </div>

          {/* Day Tabs */}
          <div style={dayTabsStyle}>
            {['M', 'T', 'W', 'Th', 'F'].map((day, index) => {
              const dayRoster = weeklyTeacherRoster[day] || {};
              const totalTeachers = Object.values(dayRoster).reduce((sum, r) => sum + r.teachers.length, 0);
              const isSelected = selectedRosterDay === day;
              
              // Get the date for this day
              const weekDatesArr = dataStore.getWeekDates(weekStart);
              const dateStr = weekDatesArr[index];
              const dateObj = new Date(dateStr + 'T12:00:00');
              const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              return (
                <button
                  key={day}
                  onClick={() => setSelectedRosterDay(day)}
                  style={dayTabStyle(isSelected)}
                >
                  <div>{day}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>{formattedDate}</div>
                </button>
              );
            })}
          </div>

          {/* Roster Content */}
          {(() => {
            const dayRoster = weeklyTeacherRoster[selectedRosterDay] || {};
            
            if (Object.keys(dayRoster).length === 0) {
              return (
                <div style={emptyStateStyle}>
                  <p>No teachers scheduled</p>
                </div>
              );
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {Object.entries(dayRoster).map(([roomId, { roomName, teachers, studentCount, isUtility }]) => (
                  <div key={roomId} style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {/* Room Header - Clickable to toggle */}
                    <div 
                      onClick={() => toggleRoomExpanded(roomId)}
                      style={{
                        padding: '10px 12px',
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        color: theme.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>🏫</span>
                        {roomName}
                        {!isUtility && (
                          <span style={{
                            fontSize: '0.75rem',
                            color: theme.textSecondary,
                            backgroundColor: theme.backgroundSecondary,
                            padding: '2px 8px',
                            borderRadius: '10px'
                          }}>
                            Students: {studentCount || 0}
                          </span>
                        )}
                      </div>
                      <span style={{ color: theme.textSecondary, fontSize: '0.9rem' }}>
                        {expandedRooms[roomId] ? '▼' : '▶'}
                      </span>
                    </div>
                    
                    {/* Teachers List - Collapsible */}
                    {expandedRooms[roomId] && (
                    <div style={{ padding: '8px 12px' }}>
                      {teachers.map((teacher, idx) => (
                        <div key={idx} style={{
                          padding: '6px 0',
                          borderBottom: idx < teachers.length - 1 ? `1px solid ${theme.border}` : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ color: theme.text, fontSize: '0.85rem' }}>
                            {teacher.name}
                          </span>
                          <span style={{ color: theme.textSecondary, fontSize: '0.75rem' }}>
                            {formatTime(teacher.startTime)} - {formatTime(teacher.endTime)}
                          </span>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Out This Week Card */}
        <div style={mobileCardStyle}>
          <div style={mobileCardTitleStyle}>
            <span>🏖️</span>
            Out This Week
          </div>
          
          {weekPTO.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>✅ No one out this week</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {weekPTO.map((pto, idx) => (
                <div key={`${pto.oderId}-${pto.date}-${idx}`} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 0',
                  borderBottom: idx < weekPTO.length - 1 ? `1px solid ${theme.border}` : 'none'
                }}>
                  <span style={{ 
                    fontSize: '0.8rem', 
                    fontWeight: '600', 
                    color: colors.blue,
                    minWidth: '50px'
                  }}>
                    {formatDateDisplay(pto.date)}
                  </span>
                  <span style={{ fontSize: '0.9rem', color: theme.text }}>
                    {pto.userName}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule Issues Card */}
        <div style={mobileCardStyle}>
          <div style={mobileCardTitleStyle}>
            <span>⚠️</span>
            Schedule Issues
            {ratioViolations.length > 0 && (
              <span style={{
                backgroundColor: colors.red,
                color: '#fff',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '0.7rem',
                fontWeight: '600'
              }}>
                {ratioViolations.length}
              </span>
            )}
          </div>
          
          {ratioViolations.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>🎉 All caught up!</p>
              <p style={{ fontSize: '0.8rem' }}>No ratio issues this week</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ratioViolations.slice(0, 5).map((violation, idx) => (
                <div key={idx} style={{
                  padding: '10px',
                  backgroundColor: darkMode ? 'rgba(239,83,80,0.15)' : 'rgba(239,83,80,0.1)',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${colors.red}`
                }}>
                  <div style={{ fontWeight: '600', fontSize: '0.85rem', color: theme.text }}>
                    📋 {violation.roomName}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '4px' }}>
                    {formatDateDisplay(violation.date)}: {violation.childCount}/{violation.maxChildren} students 
                    ({violation.teacherCount} teacher{violation.teacherCount !== 1 ? 's' : ''})
                  </div>
                </div>
              ))}
              {ratioViolations.length > 5 && (
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: theme.textSecondary }}>
                  +{ratioViolations.length - 5} more issues
                </div>
              )}
            </div>
          )}
        </div>

        {/* PTO Management Card */}
        <div style={mobileCardStyle}>
          <div style={mobileCardTitleStyle}>
            <span>📋</span>
            PTO Requests
          </div>

          {/* Filter Tabs */}
          <div style={{ 
            display: 'flex', 
            gap: '6px', 
            marginBottom: '12px',
            flexWrap: 'wrap'
          }}>
            {[
              { label: 'Pending', count: pendingCount },
              { label: 'Approved', count: approvedCount },
              { label: 'Denied', count: deniedCount },
              { label: 'All', count: allPTORequests.length }
            ].map(({ label, count }) => (
              <button
                key={label}
                onClick={() => setPtoFilter(label)}
                style={filterTabStyle(ptoFilter === label)}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          {/* PTO Request List */}
          {filteredPTORequests.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>No {ptoFilter === 'All' ? '' : ptoFilter.toLowerCase()} PTO requests</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredPTORequests.slice(0, 10).map(request => (
                <div key={request.id} style={ptoRequestCardStyle}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: theme.text, fontSize: '0.95rem' }}>
                        👤 {request.userName}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '4px' }}>
                        {formatDateRanges(request.dates)}
                        {request.isPartialDay && (
                          <span style={{ marginLeft: '8px', color: colors.blue }}>
                            ⏰ {formatTime(request.partialStartTime)} ({request.partialHours}h)
                          </span>
                        )}
                      </div>
                      {request.reason && (
                        <div style={{ fontSize: '0.8rem', color: theme.textSecondary, marginTop: '2px' }}>
                          Reason: {request.reason}
                        </div>
                      )}
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: '600',
                      backgroundColor: request.status === 'Approved' ? colors.green : 
                                       request.status === 'Denied' ? colors.red : colors.pink,
                      color: request.status === 'Pending' ? '#333' : (darkMode ? '#1a1a2e' : '#fff')
                    }}>
                      {request.status}
                    </span>
                  </div>

                  {/* Action Buttons for Pending */}
                  {request.status === 'Pending' && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        onClick={() => handleApprovePTO(request.id)}
                        style={actionButtonStyle(colors.green)}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handleDenyPTO(request.id)}
                        style={actionButtonStyle(colors.red)}
                      >
                        ✗ Deny
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {filteredPTORequests.length > 10 && (
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: theme.textSecondary }}>
                  +{filteredPTORequests.length - 10} more requests
                </div>
              )}
            </div>
          )}
        </div>

        {/* Monthly Team PTO Card */}
        <div style={mobileCardStyle}>
          <div style={mobileCardTitleStyle}>
            <span>👥</span>
            Team PTO - Month View
          </div>

          {/* Month Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <button
              onClick={prevPTOMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ‹
            </button>
            <span style={{ fontWeight: '600', fontSize: '0.9rem', color: theme.text }}>
              {teamPTOMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={nextPTOMonth}
              style={{
                padding: '6px 12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              ›
            </button>
          </div>

          {/* PTO Entries */}
          {teamPTOEntries.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>No approved PTO this month</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              {teamPTOEntries.map(entry => (
                <div key={entry.id} style={{
                  padding: '8px 10px',
                  backgroundColor: theme.backgroundSecondary,
                  borderRadius: '6px',
                  borderLeft: `3px solid ${colors.purple || '#9b59b6'}`,
                  fontSize: '0.85rem',
                  color: theme.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ fontWeight: '600', color: colors.purple || '#9b59b6' }}>
                    {entry.startDate === entry.endDate
                      ? formatDateShort(entry.startDate)
                      : `${formatDateShort(entry.startDate)} - ${formatDateShort(entry.endDate)}`
                    }
                  </span>
                  <span>{entry.userName}</span>
                  {entry.isPartialDay && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: theme.textSecondary,
                      backgroundColor: theme.hover || theme.backgroundSecondary,
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {formatTime(entry.partialStartTime)} ({entry.partialHours}h)
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // =============================================
  // DESKTOP RENDER
  // =============================================
  
  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div style={containerStyle}>
      {/* Welcome Header Banner */}
      <div style={headerBannerStyle}>
        <div style={welcomeRowStyle}>
          <div>
            <h1 style={welcomeTitleStyle}>Welcome back, {user?.name}! 👋</h1>
            <p style={dateStyle}>{dateDisplay}</p>
          </div>
        </div>
      </div>
      
      {/* Quick Stats Row */}
      <div style={statsRowStyle}>
        <div 
          style={statCardStyle(colors.green)}
          onClick={() => navigate('/admin/students')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={statValueStyle(colors.green)}>{stats.totalStudents}</div>
          <div style={statLabelStyle}>Students</div>
        </div>
        <div 
          style={statCardStyle(colors.blue)}
          onClick={() => navigate('/admin/users')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={statValueStyle(colors.blue)}>{stats.totalTeachers}</div>
          <div style={statLabelStyle}>Teachers</div>
        </div>
        <div 
          style={statCardStyle(colors.purple || '#9b59b6')}
          onClick={() => navigate('/admin/rooms')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={statValueStyle(colors.purple || '#9b59b6')}>{stats.totalRooms}</div>
          <div style={statLabelStyle}>Rooms</div>
        </div>
        <div 
          style={statCardStyle(stats.pendingPTO > 0 ? '#FF8A65' : colors.green)}
          onClick={() => navigate('/admin/pto')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={statValueStyle(stats.pendingPTO > 0 ? '#FF8A65' : colors.green)}>
            {stats.pendingPTO}
          </div>
          <div style={statLabelStyle}>Pending PTO</div>
        </div>
      </div>


      {/* Widget Grid */}
      <div style={gridStyle}>
        {/* Today's Room Coverage - spans 4 columns */}
        <div style={widgetStyle(4)}>
          <div style={widgetHeaderStyle}>
            <div style={widgetTitleStyle}>
              <span>🏠</span> Today's Coverage
            </div>
            <span style={viewAllLinkStyle} onClick={() => navigate('/admin/schedule')}>
              View Schedule →
            </span>
          </div>
          
          {todaySchedule.dayName === 'Saturday' || todaySchedule.dayName === 'Sunday' ? (
            <div style={emptyStateStyle}>
              <p>🎉 It's the weekend!</p>
            </div>
          ) : (
            <div>
              {todaySchedule.roomCoverage.map((room, idx) => (
                <div key={room.roomId} style={{
                  ...roomRowStyle,
                  borderBottom: idx === todaySchedule.roomCoverage.length - 1 ? 'none' : roomRowStyle.borderBottom
                }}>
                  <span style={roomNameStyle}>{room.roomName}</span>
                  <span style={coverageBadgeStyle(room.assignmentCount)}>
                    {room.assignmentCount === 0 ? 'No coverage' : 
                     room.assignmentCount === 1 ? '1 teacher' : 
                     `${room.assignmentCount} teachers`}
                  </span>
                </div>
              ))}
              {todaySchedule.roomCoverage.length === 0 && (
                <div style={emptyStateStyle}>No rooms configured</div>
              )}
            </div>
          )}
        </div>
        
        {/* This Week's PTO - spans 4 columns */}
        <div style={widgetStyle(4)}>
          <div style={widgetHeaderStyle}>
            <div style={widgetTitleStyle}>
              <span>🏖️</span> Out This Week
            </div>
            <span style={viewAllLinkStyle} onClick={() => navigate('/admin/pto')}>
              View All →
            </span>
          </div>
          
          {weekPTO.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>✅ No one out this week</p>
            </div>
          ) : (
            <div>
              {weekPTO.slice(0, 6).map((pto, idx) => (
                <div key={`${pto.oderId}-${pto.date}-${idx}`} style={{
                  ...ptoItemStyle,
                  borderBottom: idx === Math.min(weekPTO.length - 1, 5) ? 'none' : ptoItemStyle.borderBottom
                }}>
                  <span style={ptoDateStyle}>{formatDateDisplay(pto.date)}</span>
                  <span style={ptoNameStyle}>{pto.userName}</span>
                </div>
              ))}
              {weekPTO.length > 6 && (
                <div style={{ textAlign: 'center', padding: '8px', color: theme.textSecondary, fontSize: '0.8rem' }}>
                  +{weekPTO.length - 6} more
                </div>
              )}
            </div>
          )}
        </div>

        
        {/* Needs Attention (Schedule Issues Only) - spans 4 columns */}
        <div style={widgetStyle(4)}>
          <div style={widgetHeaderStyle}>
            <div style={widgetTitleStyle}>
              <span>⚠️</span> Schedule Issues
              {ratioViolations.length > 0 && (
                <span style={{
                  backgroundColor: colors.red,
                  color: '#fff',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  {ratioViolations.length}
                </span>
              )}
            </div>
          </div>
          
          {ratioViolations.length === 0 ? (
            <div style={emptyStateStyle}>
              <p>🎉 All caught up!</p>
              <p style={{ fontSize: '0.8rem' }}>No ratio issues this week</p>
            </div>
          ) : (
            <div>
              {/* Ratio Violations */}
              {ratioViolations.slice(0, 5).map((violation, idx) => (
                <div key={`ratio-${idx}`} style={pendingItemStyle}>
                  <div style={pendingInfoStyle}>
                    <div style={pendingNameStyle}>📋 {violation.roomName} Over Ratio</div>
                    <div style={pendingDatesStyle}>
                      {formatDateDisplay(violation.date)}: {violation.childCount} students / {violation.maxChildren} max ({violation.teacherCount} teacher{violation.teacherCount !== 1 ? 's' : ''})
                    </div>
                  </div>
                  <button 
                    style={reviewButtonStyle}
                    onClick={() => navigate('/admin/schedule')}
                  >
                    Fix
                  </button>
                </div>
              ))}
              {ratioViolations.length > 5 && (
                <div 
                  style={{ textAlign: 'center', padding: '6px', color: colors.orange, fontSize: '0.8rem', cursor: 'pointer' }}
                  onClick={() => navigate('/admin/schedule')}
                >
                  +{ratioViolations.length - 5} more ratio issues →
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
